import { Button, withStyles, CircularProgress } from '@material-ui/core';
import Card from './Card';
import { FIELD_LABEL_MAPPING } from './constants';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import { format } from 'date-fns';
import appConstants from '../../../../../appConstants';
import { withUserProfile } from '../../../../../userProfile/userProfileContext';
import { withFeatureFlags } from '../../../../../userProfile/featureFlagsContext';
import { getButtonLabel } from './summaryUtils';
import useCreateRoutes from './useCreateRoutes';

const styles = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'center'
  },
  leftSection: {
    marginRight: '8%'
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 40,
    color: theme.dispatchPanelLabelValueColor,
    display: 'flex'
  },
  headerSubTitle: {
    fontSize: 24,
    color: theme.dispatchPanelLabelValueColor
  },
  totalCount: {
    fontWeight: 600
  },
  button: {
    fontSize: 24,
    width: 224,
    height: 60,
    marginTop: 28,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    },
    '&$buttonDisabled': {
      backgroundColor: theme.disableButtonBG,
      color: theme.disableButtonText
    }
  },
  buttonDisabled: {},
  rightSection: {
    maxWidth: '55%',
    display: 'flex',
    flexWrap: 'wrap'
  },
  cardWrapper: {
    margin: '8px 8px'
  },
  planDateContainer: {
    padding: '4px 14px',
    fontSize: theme.headerTextFontSize16,
    border: `1px solid ${theme.AppBGColor}`,
    height: 'fit-content',
    borderRadius: '20px',
    marginLeft: 16,
    marginTop: 8,
    fontWeight: 500
  },
  planDateLabel: {
    color: theme.dispatchPanelHeaderColor
  },
  planDateValue: {
    color: theme.dispatchPanelLabelValueColor,
    fontWeight: 600
  },
  circularProgress: {
    marginRight: 8,
    color: theme.palette.primary.contrastText
  },
  prefetchingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 0'
  },
  prefetchingText: {
    fontSize: theme.textFontSize,
    color: theme.gridTextColor,
    fontStyle: 'italic'
  }
});

const SummaryDetails = ({ etlSummary, enqueueSnackbar, userData, classes, featureFlags, onCloseSummary }) => {
  const isEnforcedDynamicRoutingEnabled = featureFlags?.isFeatureEnabled?.(
    appConstants.features.EnableEnforcedDynamicRoutingFromShipmentSelection
  );

  const { isLoading, isPrefetching, handleCreateRoutes } = useCreateRoutes({
    etlSummary,
    userData,
    isEnforcedDynamicRoutingEnabled,
    onCloseSummary,
    enqueueSnackbar
  });

  const buttonLabel = getButtonLabel({ isEnforcedDynamicRoutingEnabled, cityRoutes: etlSummary?.cityRoutes, isLoading });

  return (
    <div className={classes.container}>
      <div id="planSummaryLeft" className={classes.leftSection}>
        <div className={classes.headerTitle}>
          Plan Summary
          <div className={classes.planDateContainer}>
            <span className={classes.planDateLabel}>Plan date </span>
            <span className={classes.planDateValue}>
              {format(etlSummary?.planDate, appConstants.DATETYPES.MONTHDATEYEAR.format)}
            </span>
          </div>
        </div>
        <div className={classes.headerSubTitle}>
          Total Shipments in Plan
          <span className={classes.totalCount}> {etlSummary.totalShipmentsInPlan}</span>
        </div>
        <Button
          color="primary"
          variant="contained"
          classes={{ root: classes.button, disabled: classes.buttonDisabled }}
          onClick={handleCreateRoutes}
          disabled={isPrefetching || isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={20} className={classes.circularProgress} />
              Opening...
            </>
          ) : (
            buttonLabel
          )}
        </Button>
        {isPrefetching && (
          <div className={classes.prefetchingContainer}>
            <CircularProgress size={14} />
            <span className={classes.prefetchingText}>Updating scenario, please wait...</span>
          </div>
        )}
      </div>
      <div id="planSummaryRight" className={classes.rightSection}>
        {Object.keys(etlSummary).map((key) => {
          if (FIELD_LABEL_MAPPING[key]) {
            return (
              <div className={classes.cardWrapper} key={key}>
                <Card label={FIELD_LABEL_MAPPING[key]} value={etlSummary[key]} />
              </div>
            );
          }
        })}
      </div>
    </div>
  );
};

export default compose(withSnackbar, withUserProfile, withFeatureFlags, withStyles(styles))(SummaryDetails);
